import * as THREE from "three";
import type { Material, PhysicalMaterial, PhongMaterial, StandardMaterial } from "../types/scene";
import type { Vector4 } from "../types/primitives";

export function createCollisionMaterial(): THREE.MeshBasicMaterial {
  const material = new THREE.MeshBasicMaterial({
    color: 0xff2222,
    opacity: 1,
    transparent: false,
    wireframe: false,
    side: THREE.DoubleSide,
  });
  material.name = "collision"; // Set the material name for identification
  material.userData = { ...material.userData, isCollisionGeomMaterial: true };
  return material;
}

const getColor = (vector?: Vector4 | null): [number, number, number, number] => {
  const base: Vector4 = Array.isArray(vector) ? vector : [0.8, 0.8, 0.8, 1];
  return [
    Number.isFinite(base[0]) ? base[0] : 0.8,
    Number.isFinite(base[1]) ? base[1] : 0.8,
    Number.isFinite(base[2]) ? base[2] : 0.8,
    Number.isFinite(base[3]) ? base[3] : 1,
  ];
};

type TextureRole =
  | "color"
  | "alpha"
  | "normal"
  | "roughness"
  | "metalness"
  | "ao"
  | "emissive"
  | "bump"
  | "displacement"
  | "light"
  | "env"
  | "data";

const colorTextureRoles = new Set<TextureRole>(["color", "emissive", "env"]);

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

// `reflectivity` is declared on PhongMaterial, but the MJCF parser also stamps
// it onto standard/physical materials when the source XML uses the
// `reflectance` attribute. Read it through an `in` narrow so we cover both
// shapes without losing type safety.
const getMaterialReflectivity = (matInfo: Material): number => {
  const raw = "reflectivity" in matInfo ? matInfo.reflectivity : undefined;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? clamp01(value) : 0;
};

export function applyTextureRoleSettings(texture: THREE.Texture | null, role: TextureRole): void {
  if (!texture) return;
  texture.colorSpace = colorTextureRoles.has(role) ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  texture.needsUpdate = true;
}

// Three.js material classes that share the set of common texture maps applied
// by `applyCommonMaps` (alpha/ao/light/normal/bump/displacement).
type MappedMaterial =
  | THREE.MeshStandardMaterial
  | THREE.MeshPhysicalMaterial
  | THREE.MeshPhongMaterial;

interface CommonMaterialProps {
  color: THREE.Color;
  opacity: number;
  transparent: boolean;
  alphaTest: number;
  depthWrite: boolean;
  side: THREE.Side;
  map?: THREE.Texture;
}

const buildMaterial = (
  materialName: string,
  matInfo: Material,
  resolveTexture: (key?: string | null, role?: TextureRole) => THREE.Texture | null
): THREE.Material => {
  const [r, g, b, a] = getColor(matInfo.color);
  const opacity = typeof matInfo.opacity === "number" ? matInfo.opacity : a;
  const alphaMode = matInfo.alphaMode ?? (matInfo.transparent || opacity < 1 ? "blend" : "opaque");
  const transparent = alphaMode === "blend" || opacity < 1;
  const alphaTest = alphaMode === "mask" ? Number(matInfo.alphaCutoff ?? 0.5) : 0;

  const commonProps: CommonMaterialProps = {
    color: new THREE.Color(r, g, b),
    opacity,
    transparent,
    alphaTest,
    depthWrite: !transparent,
    side: matInfo.doubleSided === false ? THREE.FrontSide : THREE.DoubleSide,
  };
  const reflectivity = getMaterialReflectivity(matInfo);
  if (reflectivity > 0 && r < 0.02 && g < 0.02 && b < 0.02) {
    commonProps.color = new THREE.Color(0.02, 0.02, 0.02);
  }

  const applyCommonMaps = (target: MappedMaterial): void => {
    if (matInfo.alphaMap) {
      const alpha = resolveTexture(matInfo.alphaMap, "alpha");
      if (alpha) {
        applyTextureRoleSettings(alpha, "alpha");
        target.alphaMap = alpha;
      }
    }
    if (matInfo.aoMap) {
      const ao = resolveTexture(matInfo.aoMap, "ao");
      if (ao) {
        applyTextureRoleSettings(ao, "ao");
        target.aoMap = ao;
        if (matInfo.aoMapIntensity != null) {
          target.aoMapIntensity = matInfo.aoMapIntensity;
        }
      }
    }
    if (matInfo.lightMap) {
      const lm = resolveTexture(matInfo.lightMap, "light");
      if (lm) {
        applyTextureRoleSettings(lm, "light");
        target.lightMap = lm;
        if (matInfo.lightMapIntensity != null) {
          target.lightMapIntensity = matInfo.lightMapIntensity;
        }
      }
    }
    if (matInfo.normalMap) {
      const normal = resolveTexture(matInfo.normalMap, "normal");
      if (normal) {
        applyTextureRoleSettings(normal, "normal");
        target.normalMap = normal;
        if (matInfo.normalScale) {
          target.normalScale = new THREE.Vector2(matInfo.normalScale[0], matInfo.normalScale[1]);
        }
      }
    }
    if (matInfo.bumpMap) {
      const bump = resolveTexture(matInfo.bumpMap, "bump");
      if (bump) {
        applyTextureRoleSettings(bump, "bump");
        target.bumpMap = bump;
        if (matInfo.bumpScale != null) {
          target.bumpScale = matInfo.bumpScale;
        }
      }
    }
    if (matInfo.displacementMap) {
      const displacement = resolveTexture(matInfo.displacementMap, "displacement");
      if (displacement) {
        applyTextureRoleSettings(displacement, "displacement");
        target.displacementMap = displacement;
        if (matInfo.displacementScale != null) {
          target.displacementScale = matInfo.displacementScale;
        }
        if (matInfo.displacementBias != null) {
          target.displacementBias = matInfo.displacementBias;
        }
      }
    }
  };

  const mapTexture = resolveTexture(matInfo.colorMap ?? null, "color");
  if (mapTexture) {
    applyTextureRoleSettings(mapTexture, "color");
    commonProps.map = mapTexture;
  }

  let material: THREE.Material;
  if (matInfo.kind === "physical") {
    // `kind` is not a literal discriminator on the subtypes, so narrow by cast.
    const physical = matInfo as PhysicalMaterial;
    const metalness = Number.isFinite(Number(physical.metalness)) ? Number(physical.metalness) : 0;
    const baseRoughness = Number.isFinite(Number(physical.roughness))
      ? Number(physical.roughness)
      : 0.5;
    const roughness = reflectivity > 0 ? Math.min(baseRoughness, 0.03) : baseRoughness;
    const meshPhysical = new THREE.MeshPhysicalMaterial({
      ...commonProps,
      metalness,
      roughness,
      clearcoat:
        reflectivity > 0
          ? Math.max(physical.clearcoat ?? 0, reflectivity)
          : (physical.clearcoat ?? 0),
      clearcoatRoughness:
        reflectivity > 0
          ? Math.min(physical.clearcoatRoughness ?? roughness, 0.03)
          : (physical.clearcoatRoughness ?? 0),
      transmission: physical.transmission ?? 0,
      thickness: physical.thickness ?? 0,
      ior: physical.ior ?? 1.5,
    });
    applyCommonMaps(meshPhysical);

    type PhysicalMapField =
      | "roughnessMap"
      | "metalnessMap"
      | "emissiveMap"
      | "clearcoatMap"
      | "clearcoatNormalMap"
      | "clearcoatRoughnessMap"
      | "transmissionMap"
      | "thicknessMap"
      | "specularIntensityMap"
      | "specularColorMap";

    const physicalMaps: ReadonlyArray<readonly [PhysicalMapField, TextureRole]> = [
      ["roughnessMap", "roughness"],
      ["metalnessMap", "metalness"],
      ["emissiveMap", "emissive"],
      ["clearcoatMap", "data"],
      ["clearcoatNormalMap", "normal"],
      ["clearcoatRoughnessMap", "roughness"],
      ["transmissionMap", "data"],
      ["thicknessMap", "data"],
      ["specularIntensityMap", "data"],
      ["specularColorMap", "color"],
    ];
    physicalMaps.forEach(([field, role]) => {
      const key = physical[field];
      if (!key) return;
      const tex = resolveTexture(key, role);
      if (!tex) return;
      applyTextureRoleSettings(tex, role);
      // Every PhysicalMapField is typed `Texture | null` on MeshPhysicalMaterial,
      // so this view-cast is safe and avoids per-field branching.
      (meshPhysical as Record<PhysicalMapField, THREE.Texture | null>)[field] = tex;
    });
    if (physical.clearcoatNormalScale) {
      meshPhysical.clearcoatNormalScale = new THREE.Vector2(
        physical.clearcoatNormalScale[0],
        physical.clearcoatNormalScale[1]
      );
    }
    if (physical.specularIntensity != null) {
      meshPhysical.specularIntensity = physical.specularIntensity;
    }
    if (physical.specularColor) {
      meshPhysical.specularColor = new THREE.Color(
        physical.specularColor[0] ?? 1,
        physical.specularColor[1] ?? 1,
        physical.specularColor[2] ?? 1
      );
    }
    material = meshPhysical;
  } else if (matInfo.kind === "standard") {
    const standard = matInfo as StandardMaterial;
    const metalness = Number.isFinite(Number(standard.metalness)) ? Number(standard.metalness) : 0;
    const baseRoughness = Number.isFinite(Number(standard.roughness))
      ? Number(standard.roughness)
      : 0.5;
    const roughness = reflectivity > 0 ? Math.min(baseRoughness, 0.03) : baseRoughness;
    const meshStandard = new THREE.MeshStandardMaterial({
      ...commonProps,
      metalness,
      roughness,
    });
    applyCommonMaps(meshStandard);
    if (standard.roughnessMap) {
      const rm = resolveTexture(standard.roughnessMap, "roughness");
      if (rm) {
        applyTextureRoleSettings(rm, "roughness");
        meshStandard.roughnessMap = rm;
      }
    }
    if (standard.metalnessMap) {
      const mm = resolveTexture(standard.metalnessMap, "metalness");
      if (mm) {
        applyTextureRoleSettings(mm, "metalness");
        meshStandard.metalnessMap = mm;
      }
    }
    if (standard.emissive) {
      const [er, eg, eb] = standard.emissive;
      meshStandard.emissive = new THREE.Color(er ?? 0, eg ?? 0, eb ?? 0);
    }
    if (standard.emissiveIntensity != null) {
      meshStandard.emissiveIntensity = standard.emissiveIntensity;
    }
    if (standard.emissiveMap) {
      const em = resolveTexture(standard.emissiveMap, "emissive");
      if (em) {
        applyTextureRoleSettings(em, "emissive");
        meshStandard.emissiveMap = em;
      }
    }
    if (standard.envMap) {
      const env = resolveTexture(standard.envMap, "env");
      if (env) {
        applyTextureRoleSettings(env, "env");
        meshStandard.envMap = env;
        if (standard.envMapIntensity != null) {
          meshStandard.envMapIntensity = standard.envMapIntensity;
        }
      }
    }
    material = meshStandard;
  } else {
    // Phong is the default fallback; basic/lambert domain materials are
    // approximated here as Phong since the viewer doesn't render them distinctly.
    const phong = matInfo as PhongMaterial;
    const phongReflectivity = reflectivity > 0 ? reflectivity : (phong.reflectivity ?? 0.5);
    const meshPhong = new THREE.MeshPhongMaterial({
      ...commonProps,
      specular: new THREE.Color(
        reflectivity > 0 ? 1 : (phong.specular?.[0] ?? 0.2),
        reflectivity > 0 ? 1 : (phong.specular?.[1] ?? phong.specular?.[0] ?? 0.2),
        reflectivity > 0 ? 1 : (phong.specular?.[2] ?? phong.specular?.[0] ?? 0.2)
      ),
      shininess: reflectivity > 0 ? Math.max(phong.shininess ?? 0, 180) : (phong.shininess ?? 32),
      reflectivity: phongReflectivity,
      refractionRatio: phong.refractionRatio ?? 0.98,
    });
    applyCommonMaps(meshPhong);
    if (phong.specularMap) {
      const sm = resolveTexture(phong.specularMap, "data");
      if (sm) {
        applyTextureRoleSettings(sm, "data");
        meshPhong.specularMap = sm;
      }
    }
    material = meshPhong;
  }

  material.name = materialName;
  applyMaterialTransparencySettings(material, matInfo);
  material.needsUpdate = true;
  return material;
};

const applyMaterialTransparencySettings = (material: THREE.Material, matInfo: Material): void => {
  const [, , , a] = getColor(matInfo.color);
  const opacity = typeof matInfo.opacity === "number" ? matInfo.opacity : a;
  const alphaMode = matInfo.alphaMode ?? (matInfo.transparent || opacity < 1 ? "blend" : "opaque");
  const transparent = alphaMode === "blend" || opacity < 1;
  const usesTextureAlpha = Boolean(matInfo.colorMap || matInfo.alphaMap);
  material.opacity = opacity;
  material.transparent = transparent;
  material.alphaTest = alphaMode === "mask" ? Number(matInfo.alphaCutoff ?? 0.5) : 0;
  material.polygonOffset = usesTextureAlpha && alphaMode !== "opaque";
  material.polygonOffsetFactor = usesTextureAlpha && alphaMode !== "opaque" ? -1 : 0;
  material.polygonOffsetUnits = usesTextureAlpha && alphaMode !== "opaque" ? -1 : 0;
  material.side = matInfo.doubleSided === false ? THREE.FrontSide : THREE.DoubleSide;
  material.depthWrite = !transparent;

  // Scene.jsx temporarily forces opacity/transparent on all visual meshes when
  // joint/site/collision overlays are on; stash MJCF-driven values so we can restore.
  const u = material.userData || {};
  material.userData = u;
  u.critterBaseOpacity = opacity;
  u.critterBaseTransparent = transparent;
  u.critterBaseDepthWrite = material.depthWrite;
  u.critterBaseAlphaTest = material.alphaTest;
  u.critterBasePolygonOffset = material.polygonOffset;
  u.critterBasePolygonOffsetFactor = material.polygonOffsetFactor;
  u.critterBasePolygonOffsetUnits = material.polygonOffsetUnits;
};

/**
 * Per-material wrap/repeat and UV orientation for color maps.
 */
export function applyColorMapToTexture(
  mapTexture: THREE.Texture | null,
  materialDef: Material
): void {
  if (!mapTexture) return;
  const wrapMode = materialDef.colorMapWrap === "repeat" ? "repeat" : "clamp";
  if (wrapMode === "repeat") {
    mapTexture.wrapS = THREE.RepeatWrapping;
    mapTexture.wrapT = THREE.RepeatWrapping;
  } else {
    mapTexture.wrapS = THREE.ClampToEdgeWrapping;
    mapTexture.wrapT = THREE.ClampToEdgeWrapping;
  }
  const rawRepeat = materialDef.colorMapRepeat;
  const rawOffset = materialDef.colorMapOffset;
  const rawCenter = materialDef.colorMapCenter;
  const rx = Number(Array.isArray(rawRepeat) ? rawRepeat[0] : 1);
  const ry = Number(Array.isArray(rawRepeat) ? rawRepeat[1] : 1);
  const ox = Number(Array.isArray(rawOffset) ? rawOffset[0] : 0);
  const oy = Number(Array.isArray(rawOffset) ? rawOffset[1] : 0);
  const cx = Number(Array.isArray(rawCenter) ? rawCenter[0] : 0.5);
  const cy = Number(Array.isArray(rawCenter) ? rawCenter[1] : 0.5);
  const rotation = Number(materialDef.colorMapRotation ?? 0);
  mapTexture.repeat.set(Number.isFinite(rx) ? rx : 1, Number.isFinite(ry) ? ry : 1);
  mapTexture.offset.set(Number.isFinite(ox) ? ox : 0, Number.isFinite(oy) ? oy : 0);
  mapTexture.center.set(Number.isFinite(cx) ? cx : 0.5, Number.isFinite(cy) ? cy : 0.5);
  mapTexture.rotation = Number.isFinite(rotation) ? rotation : 0;

  mapTexture.needsUpdate = true;
}

export { buildMaterial, applyMaterialTransparencySettings };
